import { getHourHorizontalLineTestId } from './testids.consts';
import { AtoZTheme } from '@amzn/atoz-web/Theme';
export var TimeConstants = {
    minutesPerHour: 60,
    hoursPerDay: 24,
    daysPerWeek: 7
};
export var DimensionConstants = {
    // Add some pixels distance between days to help create a distinct boundary between days, enhancing the visual separation and clarify within the layout.
    dayColumnGap: 16,
    // Represent the first column, displaying the hours from 1 AM to 11 PM
    hourLabelColumn: {
        /*
        Define the width of the column containing hour labels. This column has a fixed width that remains unchanged regardless of the screen size.
        It is intentionally designed this way to simplify the logic.
        */
        width: 57
    },
    /*
    The number of pixels allocated per minute on the grid. The value of 1.5 pixels per minute is specifically chosen to provide
    just sufficient space for displaying the icon(height: 16px) of a 15 minute event.
    */
    pixelsPerMinute: 1.5,
    // Minimum event block size where its icon will be shown
    eventIconHideThreshold: 15,
    // Represent the vertical scrollbar that enables scrolling to view the entire 24-hour range vertically.
    scrollbar: {
        // Represent the width of the scrollbar
        width: 15
    },
    hourHorizontalLine: {
        // Indicates the height of the horizontal hour line using pixel units
        height: 1
    },
    numOfVisibleHours: 7,
    /*
    edgeBuffer is applied to 2 use cases to allow user to comfortably see the event edges
     - when grid automatically scroll to an event, we maintain this buffer space at the top of the viewing event
     - when user scrolls all the way up/down to 12 am line, we apply this buffer space at the top/bottom of 12 am lines.
    */
    edgeBuffer: 5
};
export var EventBlockDimensions = {
    // Spacing between the icon and the edge of the event block
    padding: 3,
    // Stencil IconSize enum value
    iconSize: 16,
    borderWidth: AtoZTheme.space['S100']
};
export var EventBlockGridDimensions = {
    // The border itself is no longer a row - without it, everything appears off by one and events that start at 0:00 not on the calendar at all
    rowOffset: 1,
    // The column offset is set to 1, specifically to account for the hour label column
    columnOffset: 1,
    /*
    By subtracting the width of the hour label column, we ensures that the event blocks are properly positioned within the grid, avoiding overlap
    with the hour labels.
    */
    width: "calc(100% - ".concat(DimensionConstants.hourLabelColumn.width, "px)"),
    padding: "".concat(DimensionConstants.edgeBuffer +
        DimensionConstants.hourHorizontalLine.height, "px 0px"),
    minute: {
        height: DimensionConstants.pixelsPerMinute,
        //Each minute occupies one row within the event block grid
        numsOfRow: 1
    },
    hour: {
        height: DimensionConstants.pixelsPerMinute * TimeConstants.minutesPerHour,
        numsOfRow: 1 * TimeConstants.minutesPerHour
    },
    day: {
        height: DimensionConstants.pixelsPerMinute *
            TimeConstants.minutesPerHour *
            TimeConstants.hoursPerDay,
        numsOfRow: 1 * TimeConstants.minutesPerHour * TimeConstants.hoursPerDay
    },
    // The grid layout will have a total of 60 x 24(1440) rows, with each row represents a minute and each row will have a height of <<DimensionConstants.pixelsPerMinute>> pixels
    gridTemplateRow: "repeat(".concat(TimeConstants.minutesPerHour * TimeConstants.hoursPerDay, ", ").concat(DimensionConstants.pixelsPerMinute, "px)"),
    /*
    The grid layout will have a total of 8 columns. The first column will have a fixed width of the hour label column, and the remaining 7 columns will automatically
    distribute the remaining width equally among them, representing each day of the week.
    */
    gridTemplateColumns: "".concat(DimensionConstants.hourLabelColumn.width, "px repeat(").concat(TimeConstants.daysPerWeek, ", calc(1/").concat(TimeConstants.daysPerWeek, " * 100%))"),
    eventBlockWrapper: {
        padding: "0px ".concat(DimensionConstants.dayColumnGap / 2, "px"),
        // Add 2 pixels margin at bottom to create a distinct boundary between event blocks, should be same as value added in minHeightForIcon
        marginBottom: 2
    },
    minHeightForIcon: EventBlockDimensions.padding + EventBlockDimensions.iconSize + 2
};
export var HourGridDimensions = {
    hourLabelColumn: {
        width: DimensionConstants.hourLabelColumn.width
    },
    hourHorizontalLine: {
        height: DimensionConstants.hourHorizontalLine.height
    },
    row: {
        /*
        To simplify calculations, the padding of each hour row is set to the desired height minus the thickness of the hour horizontal line.
        This approach helps ensure that the total space occupied by hour row, including its content and horizontal hour boundary line, matches
        the desired height
        */
        paddingTop: DimensionConstants.pixelsPerMinute * TimeConstants.minutesPerHour -
            DimensionConstants.hourHorizontalLine.height,
        height: DimensionConstants.pixelsPerMinute * TimeConstants.minutesPerHour
    },
    width: '100%',
    /*
        The grid layout will have a total of 26 rows.
        The first and last rows serve as edge buffers.
        Each of the middle 24 rows represents an hour and each each row will have a height of <<EventBlockGridDimensions.hour.height>> pixels
    */
    gridTemplateRow: "".concat(DimensionConstants.edgeBuffer, "px repeat(").concat(TimeConstants.hoursPerDay, ", ").concat(EventBlockGridDimensions.hour.height, "px) ").concat(DimensionConstants.edgeBuffer, "px"),
    /*
    The grid layout will have a total of 8 columns. The first column will have a fixed width of the hour label column, and the remaining 7 columns will automatically
    distribute the remaining width equally among them, representing each day of the week.
    */
    gridTemplateColumns: "".concat(DimensionConstants.hourLabelColumn.width, "px repeat(").concat(TimeConstants.daysPerWeek, ", calc(1/").concat(TimeConstants.daysPerWeek, " * 100%))"),
    twelveAmHourLabelEdgeBuffer: 10
};
export var WeeklyScheduleDimensions = {
    // By default, the component will automatically scroll to a position that displays the schedules starting from
    defaultScrollToElementId: getHourHorizontalLineTestId(10),
    scheduleViewContainer: {
        width: '100%'
    },
    gridContainer: {
        height: DimensionConstants.numOfVisibleHours * HourGridDimensions.row.height
    }
};
export var HeaderDimensions = {
    container: {
        portrait: {
            // 9px is from datepicker: https://code.amazon.com/packages/AtoZSpyglass/blobs/73ab28b084308065cd49bd0a915183f037e985eb/--/packages/web-components/src/Datepicker/datepicker.tsx#L153
            paddingTop: '9px'
        }
    }
};
export var WeekRowDimension = {
    marginLeft: "".concat(DimensionConstants.hourLabelColumn.width, "px"),
    marginBottom: "16px",
    width: "calc(100% - ".concat(DimensionConstants.hourLabelColumn.width, "px - ").concat(DimensionConstants.scrollbar.width, "px)"),
    day: {
        /*
        To ensure that each calendar day cell is a square, the width is equal to height. Add a 5% margin on the left and right sides to help create a
        distinct boundary between days, enhancing the visual separation and clarify within the layout.

        The use of "!important" is justified when weekly schedule component relies on datepicker component and we need to modify datepicker's style to
        suit the requirements of weekly schedule component without altering datepicker itself.
        */
        width: "calc(100% / 7 * 90 / 100) !important",
        verticalPadding: "min(calc(100% / 7 * 90 / 100 / 2), 30px) !important",
        height: "0 !important",
        margin: "0px calc(100% / 7 * 5 / 100)"
    }
};
export var FooterDimensions = {
    legendMaxWidth: 150
};
